/* RESPONSIBLE TEAM: team-actions */

interface ReasonedStep {
  section_name: string;
  step_number: string;
}

const formatStepId = (stepId: string | undefined) => {
  if (!stepId) {
    return '';
  }
  // answerbot uses main_section for the main procedure in the step number for procedures
  if (stepId?.includes('main_section')) {
    // Extract step number after "main_section."
    return stepId.replace('main_section.', '');
  } else if (stepId?.includes('.')) {
    // If there's a dot and it's not main_section, extract the step number after the dot
    let firstDotIndex = stepId.indexOf('.');
    return stepId.substring(firstDotIndex + 1);
  }
  return stepId;
};

const getSubProcedure = (
  sectionName: string | undefined,
  stepNumber: string | undefined = undefined,
) => {
  if (sectionName === 'Main Section') {
    return undefined;
  } else if (sectionName) {
    return sectionName;
  }
  if (!stepNumber) {
    return undefined;
  }
  let isSubProcedure = !stepNumber?.includes('main_section') && stepNumber?.includes('.');
  if (isSubProcedure) {
    return stepNumber?.substring(0, stepNumber?.indexOf('.'));
  }
  return undefined;
};

export const buildStepReasoningEvent = (
  operatorName: string,
  stepReasoning: any,
): any | undefined => {
  let reasonedSteps: ReasonedStep[] = stepReasoning?.reasoned_step_ids ?? [];
  let hasMultipleReasonedSteps =
    reasonedSteps.length > 1 &&
    reasonedSteps.every(
      (item) => typeof item.step_number === 'string' && typeof item.section_name === 'string',
    );

  let baseEvent = (overrides = {}) => ({
    operatorName,
    step_description: stepReasoning?.step_description,
    reason: stepReasoning?.reason,
    iconName: 'fin',
    type: 'step_reasoning',
    transaction_id: stepReasoning?.transaction_id,
    procedure_planner_run_id: stepReasoning?.procedure_planner_run_id,
    ...overrides,
  });

  // BASIC CASE: Single step reasoning
  if (!hasMultipleReasonedSteps) {
    let first = reasonedSteps[0];
    let stepNumber = first?.step_number ?? formatStepId(stepReasoning.step_number);
    let subProcedure = getSubProcedure(first?.section_name, stepReasoning.step_number);

    return baseEvent({
      i18nKey: subProcedure
        ? 'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning_sub_procedure'
        : 'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning',
      sub_procedure: subProcedure || undefined,
      step_number: stepNumber,
    });
  }

  // Determine presence of main section & subproc
  let hasMainSection = reasonedSteps.some((s: ReasonedStep) => s.section_name === 'Main Section');
  let hasSubProcedure = reasonedSteps.some(
    (s: ReasonedStep) => getSubProcedure(s.section_name) !== undefined,
  );

  // Helper to format plural step numbers
  let joinStepNumbers = (items: string[]) =>
    items.length < 3 ? items.join(' & ') : `${items.slice(0, -1).join(', ')} & ${items.at(-1)}`;

  // COMPLEX CASE: Main section + sub procedure
  if (hasMainSection && hasSubProcedure) {
    let stepNumbers = reasonedSteps.map((step: ReasonedStep) =>
      step.section_name === 'Main Section'
        ? step.step_number
        : `${getSubProcedure(step.section_name)} ${step.step_number}`,
    );

    return baseEvent({
      i18nKey: 'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning_plural',
      step_number: joinStepNumbers(stepNumbers),
      sub_procedure: undefined,
    });
  }

  // Common step number formatting (main-only or subproc-only)
  let formattedNumbers = joinStepNumbers(reasonedSteps.map((s: ReasonedStep) => s.step_number));

  // STANDARD CASE: Multiple reasoned steps (main)
  if (hasMainSection) {
    return baseEvent({
      i18nKey: 'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning_plural',
      step_number: formattedNumbers,
      sub_procedure: undefined,
    });
  }

  // STANDARD CASE: Multiple reasoned steps (subproc)
  if (hasSubProcedure) {
    let firstSubProcedure = reasonedSteps.find(
      (s: ReasonedStep) => s.section_name !== 'Main Section',
    )?.section_name;

    return baseEvent({
      i18nKey:
        'inbox.operator-workflow-event.events.ai_agent_task_step_reasoning_sub_procedure_plural',
      step_number: formattedNumbers,
      sub_procedure: firstSubProcedure,
    });
  }

  return undefined;
};
